import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Sparkles, Clock } from 'lucide-react';

const plans = [
  {
    name: 'Recording & Highlights',
    slug: 'recording-highlights',
    badge: null,
    sport: 'Cricket',
    description: 'Perfect for turfs wanting to provide recorded match highlights to players.',
    priceLabel: 'One-Time Installation',
    priceValue: '₹99,999',
    features: ['Match Recording', 'Highlight Generation', 'Cloud Storage', 'Mobile App Access', 'Email Support'],
    popular: false,
    cta: 'Get Started',
    color: '#4FC3F7',
    recurring: 'Rs 19,999/month SaaS & maintenance',
  },
  {
    name: 'Stadium Sound',
    slug: 'stadium-sound',
    badge: 'Most Popular',
    sport: 'Cricket',
    description: 'Our most popular tier for cricket turfs and academies. Includes Live AI scoring.',
    priceLabel: 'One-Time Installation',
    priceValue: '₹1,29,999',
    features: ['Live AI Scoring', 'Ball Tracking', 'Wicket Detection', 'Real-Time Analytics', 'Mobile Dashboard', 'Priority Support'],
    popular: true,
    cta: 'Get Started',
    color: '#FF4F7B',
    recurring: 'Rs 19,999/month SaaS & maintenance',
  },
  {
    name: 'Cricket-Only Turfs + Live TV Display',
    slug: 'cricket-turfs-live-tv',
    badge: null,
    sport: 'Cricket',
    description: 'Premium tier for professional cricket facilities with live TV display integration.',
    priceLabel: 'One-Time Installation',
    priceValue: '₹1,59,999',
    features: ['Stadium Display', 'Live TV Integration', 'Multi-Camera Setup', 'Advanced Analytics', 'Custom Branding', 'Dedicated Support'],
    popular: false,
    cta: 'Get Started',
    color: '#FF9A3C',
    recurring: 'Rs 19,999/month SaaS & maintenance',
  },
  {
    name: 'Tournaments & Venues',
    slug: 'custom-price',
    badge: null,
    sport: 'Custom',
    description: 'Tailored solutions for large tournaments, stadiums, and international venues with unique requirements.',
    priceLabel: 'Contact for Quote',
    priceValue: 'Custom Pricing',
    features: ['Multi-Venue Setup', 'Custom Infrastructure', 'Live Broadcast Integration', 'Tournament Management', 'VIP Analytics Dashboard', 'Dedicated Account Manager'],
    popular: false,
    cta: 'Custom Booking',
    color: '#8B5CF6',
    recurring: 'Custom SaaS package',
  },
];

const comingSoonSports = [
  { name: 'Football', color: '#FF4F7B', icon: 'Football' },
  { name: 'Tennis', color: '#4FC3F7', icon: 'Tennis' },
  { name: 'Pickleball', color: '#22C55E', icon: 'Pickleball' },
];

export default function PricingSection() {
  const navigate = useNavigate();
  return (
    <section id="pricing" data-testid="pricing-section" className="py-12 md:py-16 px-6 md:px-12 lg:px-24 bg-gl-dark relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-gl-coral/3 blur-[150px] gsap-parallax" />
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16 gsap-heading">
          <span className="text-xs font-body font-semibold uppercase tracking-[0.2em] text-gl-orange/80">Pricing</span>
          <h2 data-testid="pricing-heading" className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white leading-tight tracking-tight mt-4">
            Choose Your<br /><span className="text-gradient-orange">Cricket Plan.</span>
          </h2>
          <p className="mt-4 text-base font-body text-white/45 max-w-2xl mx-auto leading-relaxed">
            Professional AI officiating for cricket turfs. All tiers include Rs 19,999/month SaaS & maintenance.
          </p>
        </div>

        {/* Cricket Pricing cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch gsap-stagger mb-16 mt-12">
          {plans.map((plan, i) => (
            <div key={i} data-testid={`pricing-card-${plan.name.toLowerCase().replace(/\s/g, '-')}`}
              className={`relative rounded-2xl p-6 flex flex-col transition-all duration-500 ${plan.popular ? 'dark-glass' : 'dark-glass'}`}
              style={plan.popular ? { borderColor: `${plan.color}25`, boxShadow: `0 0 40px ${plan.color}08` } : {}}>
              {plan.popular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-white text-xs font-body font-semibold shadow-lg" style={{ background: plan.color, boxShadow: `0 4px 20px ${plan.color}30` }}>
                  <Sparkles className="w-3 h-3" />{plan.badge}
                </div>
              )}

              <span className="text-xs font-body font-semibold text-white/40 mb-2">{plan.sport}</span>
              <h3 className="font-heading font-bold text-xl text-white">{plan.name}</h3>
              <p className="text-sm font-body text-white/40 mt-1.5 leading-relaxed min-h-[48px]">{plan.description}</p>

              {/* Price area */}
              <div className="mt-5 mb-1">
                <span className="block text-xs font-body font-medium uppercase tracking-wider text-white/30 mb-1">{plan.priceLabel}</span>
                <span className="font-heading font-bold text-2xl text-white">{plan.priceValue}</span>
              </div>

              {/* Recurring cost */}
              <p className="text-xs font-body text-white/35 mb-6">{plan.recurring}</p>

              {/* CTA */}
              <button data-testid={`pricing-cta-${plan.name.toLowerCase().replace(/\s/g, '-')}`} onClick={() => navigate(`/plan/${plan.slug}`)}
                className={`w-full py-3 md:py-4 rounded-xl text-sm md:text-base font-body font-semibold transition-all duration-200 flex items-center justify-center gap-2 ${
                  plan.popular ? 'text-white hover:opacity-90 shadow-lg btn-glow' : 'bg-white/5 text-white/80 hover:bg-white/8 border border-white/8 btn-glow-soft'}`}
                style={plan.popular ? { background: plan.color, boxShadow: `0 4px 20px ${plan.color}20` } : {}}>
                {plan.cta}<ArrowRight className="w-4 h-4 btn-glow-icon" />
              </button>

              {/* Features */}
              <div className="mt-6 space-y-2.5 flex-1">
                {plan.features.map((f, fi) => (
                  <div key={fi} className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: plan.popular ? plan.color : `${plan.color}90` }} />
                    <span className="text-sm font-body text-white/55">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Coming Soon Sports */}
        <div className="mt-16 border-t border-white/10 pt-16">
          <div className="text-center mb-12">
            <h3 className="font-heading font-bold text-2xl md:text-3xl text-white">Other Sports</h3>
            <p className="text-sm font-body text-white/40 mt-2">Expanding to more sports soon</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 gsap-stagger">
            {comingSoonSports.map((sport, i) => (
              <div key={i} className="relative rounded-2xl p-8 flex flex-col items-center justify-center text-center dark-glass min-h-[300px]"
                style={{ borderColor: `${sport.color}15` }}>
                <Clock className="w-8 h-8 mx-auto mb-4" style={{ color: sport.color }} />
                <h4 className="font-heading font-bold text-xl text-white">{sport.name}</h4>
                <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-gl-orange/5 to-gl-blue/5 border border-white/8">
                  <Clock className="w-4 h-4 text-gl-orange" />
                  <span className="text-sm font-body font-semibold text-white/70">Coming Soon</span>
                </div>
                <p className="text-xs font-body text-white/35 mt-4">Join our waitlist for early access</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <button onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-3 bg-gradient-brand text-white rounded-xl text-sm font-body font-semibold hover:opacity-90 transition-all inline-flex items-center gap-2 shadow-lg btn-glow">
              <Sparkles className="w-4 h-4" />
              Join Waitlist for Other Sports<ArrowRight className="w-4 h-4 btn-glow-icon" />
            </button>
          </div>
        </div>

        {/* Bottom note */}
        <div className="mt-14 text-center dark-glass rounded-2xl p-8 gsap-scale">
          <h3 className="font-heading font-bold text-xl md:text-2xl text-white">Custom Cricket Solutions?</h3>
          <p className="text-sm font-body text-white/40 mt-2 max-w-2xl mx-auto leading-relaxed">
            Every cricket facility has unique requirements. We'll create a customized solution based on your specific needs, infrastructure, and scale.
          </p>
          <button onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="mt-6 px-6 py-3 bg-gradient-brand text-white rounded-xl text-sm font-body font-semibold hover:opacity-90 transition-all inline-flex items-center gap-2 shadow-lg btn-glow">
            Get a Custom Quote<ArrowRight className="w-4 h-4 btn-glow-icon" />
          </button>
        </div>
      </div>
    </section>
  );
}
